import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  COMPARAISON_MAX,
  COMPARAISON_MIN,
  FILTRES_LISTE,
  LIBELLES_FILTRE_LISTE,
  auMoins,
  correspondAuFiltre,
  correspondAuxCriteres,
  critereActif,
  criteresDepuisRecherche,
  criteresVersRecherche,
  telephoneLisible,
  type AnnonceResume,
  type CandidatureDetail,
  type CandidatureResume,
  type ContactDetail,
  type CriteresListe as Criteres,
  type FiltreListe,
} from '@rh/shared';
import { ErreurReseau, appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { SectionFormulaire } from '../composants/SectionFormulaire';
import { BadgeAdequation } from '../composants/Adequation';
import { AnciennesCandidatures } from '../composants/AnciennesCandidatures';
import { BoutonExport } from '../composants/BoutonExport';
import { CriteresListe } from '../composants/CriteresListe';
import { DernierAppel } from '../composants/DernierAppel';
import { Etoile } from '../composants/Etoile';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { MemoCandidat } from '../composants/MemoCandidat';
import { NoterPotentiel } from '../composants/NoterPotentiel';
import { Note, Pastille } from '../composants/Pastille';
import { Signaux } from '../composants/Signaux';
import { Statut } from '../composants/Statut';
import { TriListe } from '../composants/TriListe';
import { trier, type Tri } from '../tri-liste';
import { ecrireFiltres, lireFiltres } from '../filtres-session';
import { ecrireParcours } from '../parcours';
import { classeNom, libelleSource, nomComplet } from '../format';
import { memoire } from '../horsligne/memoire';
import { MESSAGE_OUVERTURE_HORS_LIGNE } from '../horsligne/textes';

/** Écran 1 — liste par annonce : une carte par candidat, lisible en deux secondes. */
export function ListeCandidatures() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const [candidatures, setCandidatures] = useState<CandidatureResume[] | null>(null);
  const [annonce, setAnnonce] = useState<AnnonceResume | null>(null);
  const [filtre, setFiltre] = useState<FiltreListe>('toutes');
  // Critères dans l'adresse : un lien ou un retour arrière retrouve la même sélection.
  const [recherche, setRecherche] = useSearchParams();
  const criteres = useMemo(() => criteresDepuisRecherche((cle) => recherche.get(cle)), [recherche]);
  const [tri, setTri] = useState<Tri>('defaut');

  // Filtres gardés le temps de la session (demande du 2026-09-19) : ouvrir une
  // fiche puis revenir, ou repasser par une autre annonce, retrouve la même
  // sélection. Elle s'efface quand on l'annule, et à la déconnexion.
  const changerCriteres = (suivants: Criteres) => {
    const adresse = new URLSearchParams(criteresVersRecherche(suivants));
    setRecherche(adresse, { replace: true });
    ecrireFiltres(id, { filtre, criteres: adresse.toString(), tri });
  };
  const changerFiltre = (suivant: FiltreListe) => {
    setFiltre(suivant);
    ecrireFiltres(id, { filtre: suivant, criteres: recherche.toString(), tri });
  };
  const changerTri = (suivant: Tri) => {
    setTri(suivant);
    ecrireFiltres(id, { filtre, criteres: recherche.toString(), tri: suivant });
  };
  const [erreur, setErreur] = useState<string | null>(null);
  const [inclureAnciennes, setInclureAnciennes] = useState(false);
  const [rechargement, setRechargement] = useState(0);
  const [rattachee, setRattachee] = useState<CandidatureDetail | null>(null);
  const [erreurAppel, setErreurAppel] = useState<string | null>(null);
  const [aComparer, setAComparer] = useState<string[]>([]);

  // Reprise des filtres de cette annonce, une fois par annonce ouverte.
  const reprise = useRef<string | null>(null);
  useEffect(() => {
    setInclureAnciennes(false);
    setRattachee(null);
    setAComparer([]);
    if (reprise.current === id) return;
    reprise.current = id;
    const gardes = lireFiltres(id);
    setFiltre(gardes?.filtre ?? 'toutes');
    setTri(gardes?.tri ?? 'defaut');
    // L'adresse prime : un lien partagé porte ses propres critères.
    if (gardes?.criteres && !recherche.toString()) {
      setRecherche(new URLSearchParams(gardes.criteres), { replace: true });
    }
  }, [id, recherche, setRecherche]);

  useEffect(() => {
    appel<AnnonceResume[]>('/annonces')
      .then((liste) => setAnnonce(liste.find((a) => a.id === id) ?? null))
      .catch(() => setAnnonce(null));
  }, [id]);

  useEffect(() => {
    if (rechargement === 0) setCandidatures(null);
    appel<CandidatureResume[]>(`/annonces/${id}/candidatures`)
      .then(setCandidatures)
      .catch((e: Error) => setErreur(e.message));
  }, [id, rechargement]);

  // Les filtres portent sur une liste d'une annonce, déjà entière en mémoire ;
  // les nombres des filtres rapides tiennent compte des critères choisis.
  const comptes = useMemo(() => {
    const maintenant = new Date();
    const retenues = (candidatures ?? []).filter((c) => correspondAuxCriteres(criteres, c));
    return Object.fromEntries(
      FILTRES_LISTE.map((f) => [f, retenues.filter((c) => correspondAuFiltre(f, c, maintenant))]),
    ) as Record<FiltreListe, CandidatureResume[]>;
  }, [candidatures, criteres]);
  const cheminExport = `/exports/candidatures?${new URLSearchParams([
    ['annonce', id],
    ['filtre', filtre],
    ...criteresVersRecherche(criteres),
  ]).toString()}`;
  const filtreDecrit = filtre !== 'toutes' || critereActif(criteres);
  const visibles = useMemo(() => trier(comptes[filtre] ?? [], tri), [comptes, filtre, tri]);
  const rh = utilisateur ? auMoins(utilisateur.role, 'rh') : false;

  // La fiche ne peut pas recalculer cet ordre : il dépend des filtres et du
  // tri posés ici. La liste l'écrit donc en partant, pour ses deux flèches
  // (demande du 2026-09-19, `src/parcours.ts`).
  useEffect(() => {
    ecrireParcours({
      retour: `/annonces/${id}${recherche.toString() ? `?${recherche}` : ''}`,
      libelle: annonce?.intitule ?? '',
      ids: visibles.map((candidature) => candidature.id),
    });
  }, [visibles, id, recherche, annonce?.intitule]);
  const complet = aComparer.length >= COMPARAISON_MAX;

  /** Reprend sur la carte ce que le serveur vient de renvoyer, sans tout relire. */
  function majCandidature(candidatureId: string, champs: Partial<CandidatureResume>): void {
    setCandidatures(
      (liste) => liste?.map((c) => (c.id === candidatureId ? { ...c, ...champs } : c)) ?? liste,
    );
  }

  function basculerComparaison(candidatureId: string, choisi: boolean): void {
    setAComparer((liste) =>
      choisi
        ? liste.includes(candidatureId) || liste.length >= COMPARAISON_MAX
          ? liste
          : [...liste, candidatureId]
        : liste.filter((autre) => autre !== candidatureId),
    );
  }

  async function appeler(candidature: CandidatureResume): Promise<void> {
    setErreurAppel(null);
    try {
      const contact = await appel<ContactDetail>(`/candidatures/${candidature.id}/contacts`, {
        method: 'POST',
        body: JSON.stringify({ type: 'telephonique' }),
      });
      // Dernière lecture gardée en mémoire : le panneau se réaffiche si le réseau coupe ensuite.
      memoire.retenirContact(contact);
      memoire.retenirFiche({
        id: candidature.id,
        prenom: candidature.prenom,
        nom: candidature.nom,
        telephone: candidature.telephone,
        annonceIntitule: annonce?.intitule ?? candidature.annonceIntitule ?? null,
        potentiel: candidature.potentiel,
        // Une fiche anonymisée n'a plus de téléphone : elle ne s'appelle pas d'ici.
        anonymiseeLe: null,
      });
      navigate(`/contacts/${contact.id}`);
    } catch (e) {
      setErreurAppel(
        e instanceof ErreurReseau
          ? MESSAGE_OUVERTURE_HORS_LIGNE
          : e instanceof Error
            ? e.message
            : 'Ouverture de l’appel impossible',
      );
    }
  }

  return (
    <section>
      <div className="entete-annonce">
        <div>
          <h2>{annonce?.intitule ?? 'Candidatures'}</h2>
          {annonce?.canal === 'reseaux_sociaux' && (
            <span className="badge-rs" title="Publiée sur les réseaux sociaux">
              RS
            </span>
          )}{' '}
          {annonce && <code>{annonce.reference}</code>}{' '}
          <Link to={`/annonces/${id}/edition`} className="lien-annonce">
            Texte et publications
          </Link>{' '}
          <Link to={`/statistiques?annonce=${id}`} className="lien-annonce">
            Statistiques
          </Link>
        </div>
        <span className="resume">
          {candidatures
            ? critereActif(criteres)
              ? `${comptes.toutes.length} sur ${candidatures.length} candidature(s)`
              : `${candidatures.length} candidature(s)`
            : ''}
        </span>
      </div>

      {annonce && id && (
        <SectionFormulaire
          annonceId={id}
          rh={rh}
          ouverte={annonce.statut === 'validee' || annonce.statut === 'publiee'}
        />
      )}

      <div className="outils-liste">
        {candidatures && candidatures.length > 0 && (
          <CriteresListe
            liste={candidatures}
            criteres={criteres}
            nombreRetenu={visibles.length}
            onChange={changerCriteres}
          />
        )}
        <Link to={`/recherche?annonce=${id}`} className="lien-recherche">
          Rechercher un mot-clé
        </Link>
      </div>

      <div className="filtres" role="group" aria-label="Filtres rapides">
        {FILTRES_LISTE.map((f) => (
          <button
            key={f}
            type="button"
            className={f === filtre ? 'actif' : ''}
            onClick={() => changerFiltre(f)}
          >
            {LIBELLES_FILTRE_LISTE[f]}
            {candidatures && ` (${comptes[f]?.length ?? 0})`}
          </button>
        ))}
      </div>
      <TriListe tri={tri} onChange={changerTri} />
      {candidatures && visibles.length > 0 && (
        <p className="actions-liste">
          <BoutonExport
            chemin={cheminExport}
            nomParDefaut="rh-candidatures.xlsx"
            libelle={
              !filtreDecrit
                ? 'Exporter en Excel'
                : filtre !== 'toutes' && !critereActif(criteres)
                  ? `Exporter « ${LIBELLES_FILTRE_LISTE[filtre]} » en Excel`
                  : `Exporter la sélection (${visibles.length}) en Excel`
            }
          />
        </p>
      )}

      {erreur && <Erreur message={erreur} />}
      {erreurAppel && <Erreur message={erreurAppel} />}
      {!candidatures && !erreur && <Chargement quoi="des candidatures" />}
      {candidatures && visibles.length === 0 && (
        <Vide enfants={critereActif(criteres) ? 'Aucune candidature pour ces critères.' : 'Aucune candidature pour ce filtre.'} />
      )}

      <ul className="candidats">
        {visibles.map((candidature) => {
          const choisi = aComparer.includes(candidature.id);
          return (
            <li key={candidature.id} className="carte-candidat">
              {/* Pastille notable d'un clic pour un profil RH : la noter n'oblige plus
                  à ouvrir un appel (demande du 2026-09-19). */}
              {rh && !candidature.anonymisee ? (
                <NoterPotentiel
                  candidatureId={candidature.id}
                  potentiel={candidature.potentiel}
                  etiquette={candidature.etiquette}
                  nom={nomComplet(candidature)}
                  variante="pastille"
                  onChange={(fiche) =>
                    majCandidature(fiche.id, {
                      potentiel: fiche.potentiel,
                      potentielManuelLe: fiche.potentielManuelLe,
                      etiquette: fiche.etiquette,
                    })
                  }
                />
              ) : (
                <Pastille valeur={candidature.potentiel} libelle etiquette={candidature.etiquette} />
              )}
              {/* La première ligne sort du lien : le petit mot s'y modifie sur
                  place, et un champ de saisie ne vit pas dans un lien
                  (demande du 2026-09-19). Le nom reste cliquable, le reste de
                  la carte aussi. */}
              <div className="carte-lien">
                <div className="corps">
                  <div className="ligne1">
                    <Etoile potentiel={candidature.potentiel} />
                    <Link to={`/candidatures/${candidature.id}`} className="nom-lien">
                      <strong className={classeNom(candidature.statut)}>{nomComplet(candidature)}</strong>
                    </Link>
                    {/* À droite du nom (2026-09-19) : le mot ne pousse plus les
                        noms, la colonne reste alignée d'une carte à l'autre. */}
                    <MemoCandidat
                      candidatureId={candidature.id}
                      memo={candidature.memo}
                      nom={nomComplet(candidature)}
                      modifiable={rh && !candidature.anonymisee}
                      onChange={(fiche) => majCandidature(fiche.id, { memo: fiche.memo })}
                    />
                    <BadgeAdequation valeur={candidature.adequation} />
                  </div>
                  <Link to={`/candidatures/${candidature.id}`} className="reste-carte">
                  <p className="synthese">
                    {candidature.synthese?.pointsForts.slice(0, 2).join(' · ') ||
                      'Synthèse en attente d’extraction'}
                  </p>
                  <div className="meta">
                    {/* Écarté : la mention passe à droite, en face de la note (2026-09-19). */}
                    {candidature.statut !== 'ecarte' && <Statut valeur={candidature.statut} />}
                    {candidature.ville && <span>{candidature.ville}</span>}
                    {candidature.telephone && (
                      <span className="numeros">
                        {[candidature.telephone, ...candidature.autresTelephones].map(telephoneLisible).join(' · ')}
                      </span>
                    )}
                    <span>{libelleSource(candidature.source)}</span>
                    {!candidature.cvJoint && <span className="sans-cv">Sans CV</span>}
                    <span>{new Date(candidature.recueLe).toLocaleDateString('fr-FR')}</span>
                    {candidature.annonceReference && (
                      <span className="origine">{candidature.annonceReference}</span>
                    )}
                    <DernierAppel le={candidature.dernierAppelLe} joignable={candidature.dernierAppelJoignable} />
                  </div>
                  <Signaux candidature={candidature} />
                  </Link>
                </div>
                <Link to={`/candidatures/${candidature.id}`} className="coin-droit">
                  {candidature.statut === 'ecarte' && <Statut valeur={candidature.statut} />}
                  <Note valeur={candidature.noteEntretien} date={candidature.noteLe} />
                </Link>
              </div>
              {/* Un dossier écarté ne s'appelle plus : le bouton disparaît (2026-09-19). */}
              {rh && candidature.telephone && candidature.statut !== 'ecarte' && (
                <a
                  className="appeler-mini"
                  href={`tel:${candidature.telephone}`}
                  aria-label={`Appeler ${nomComplet(candidature)}`}
                  onClick={() => void appeler(candidature)}
                >
                  Appeler
                </a>
              )}
              <label className="comparer-case">
                <input
                  type="checkbox"
                  checked={choisi}
                  disabled={!choisi && complet}
                  aria-describedby={!choisi && complet ? 'limite-comparaison' : undefined}
                  aria-label={`Comparer ${nomComplet(candidature)}`}
                  onChange={(e) => basculerComparaison(candidature.id, e.target.checked)}
                />
                <span aria-hidden="true">Comparer</span>
              </label>
            </li>
          );
        })}
      </ul>

      {aComparer.length > 0 && (
        <div className="barre-comparer" role="region" aria-label="Comparaison">
          <span id="limite-comparaison">
            {aComparer.length} candidat{aComparer.length > 1 ? 's' : ''} choisi{aComparer.length > 1 ? 's' : ''}
            {complet ? ` (${COMPARAISON_MAX} au plus)` : ''}
          </span>
          <span className="export-excel">
            {aComparer.length >= COMPARAISON_MIN ? (
              <Link to={`/comparateur?candidatures=${aComparer.join(',')}`}>Comparer</Link>
            ) : (
              <span>Choisissez-en au moins {COMPARAISON_MIN}</span>
            )}
            <button type="button" className="lien" onClick={() => setAComparer([])}>
              Effacer
            </button>
          </span>
        </div>
      )}

      <label className="case interrupteur">
        <input
          type="checkbox"
          role="switch"
          checked={inclureAnciennes}
          onChange={(e) => setInclureAnciennes(e.target.checked)}
        />
        <span>Inclure les anciennes candidatures</span>
      </label>
      {rattachee && (
        <p className="notice-vivier" role="status">
          {nomComplet(rattachee)} est rattaché(e) à cette annonce.{' '}
          <Link to={`/candidatures/${rattachee.id}`}>Ouvrir la fiche</Link>
        </p>
      )}
      {inclureAnciennes && (
        <section className="bloc">
          <h3>Anciennes candidatures</h3>
          <AnciennesCandidatures
            annonceId={id}
            rh={rh}
            onRattachee={(fiche) => {
              setRattachee(fiche);
              setRechargement((n) => n + 1);
            }}
          />
        </section>
      )}
    </section>
  );
}
